/**
 * Транспорты реального времени: WebSocket (основной) и HTTP long-polling (резервный — для сетей,
 * где WebSocket режут прокси, фильтры или антивирусы). Протокол long-polling описан в
 * services/game-server/src/net/pollTransport.ts.
 *
 * Оба транспорта сообщают о себе одинаково: open → message* → close(code). close вызывается
 * ровно один раз; после него транспорт не используется.
 */
import type { ClientMessage, ServerMessage } from '@mt/protocol';

export type TransportKind = 'ws' | 'poll';

export interface TransportHandlers {
  open(): void;
  message(m: ServerMessage): void;
  close(code: number): void;
}

export interface Transport {
  readonly kind: TransportKind;
  readonly url: string;
  send(m: ClientMessage): void;
  /** закрыть без вызова handlers.close */
  dispose(): void;
}

/** Коды закрытия, выставляемые клиентом (диапазон 4000–4999 — для приложений). */
export const CLOSE_OPEN_TIMEOUT = 4000;
export const CLOSE_HEARTBEAT = 4001;
export const CLOSE_NO_HELLO = 4002;
export const CLOSE_GONE = 4410;

export function wsTransport(h: TransportHandlers, openTimeoutMs = 6000): Transport {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const url = `${proto}://${location.host}/ws`;
  const ws = new WebSocket(url);
  let done = false;
  const finish = (code: number) => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    h.close(code);
  };
  // рукопожатие, которое «висит» (прокси не отвечает на Upgrade), — тоже отказ WebSocket
  const timer = setTimeout(() => {
    if (ws.readyState !== WebSocket.OPEN) {
      finish(CLOSE_OPEN_TIMEOUT);
      ws.close();
    }
  }, openTimeoutMs);
  ws.onopen = () => {
    clearTimeout(timer);
    if (!done) h.open();
  };
  ws.onmessage = (ev) => {
    if (!done) h.message(JSON.parse(ev.data as string) as ServerMessage);
  };
  ws.onclose = (ev) => finish(ev.code);
  return {
    kind: 'ws',
    url,
    send: (m) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m));
    },
    dispose: () => {
      done = true;
      clearTimeout(timer);
      ws.close();
    },
  };
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function pollTransport(h: TransportHandlers): Transport {
  const base = `${location.origin}/api/rt`;
  const ctrl = new AbortController();
  const outbox: ClientMessage[] = [];
  let sid: string | null = null;
  let ack = 0;
  let done = false;
  let sending = false;

  const finish = (code: number) => {
    if (done) return;
    done = true;
    ctrl.abort();
    h.close(code);
  };
  const request = (path: string, init?: RequestInit) => fetch(`${base}/${path}`, { cache: 'no-store', ...init, signal: ctrl.signal });

  async function start() {
    try {
      const r = await request('open', { method: 'POST' });
      if (!r.ok) return finish(1006);
      sid = ((await r.json()) as { sid: string }).sid;
    } catch {
      return finish(1006);
    }
    if (done) return;
    h.open();
    void flush();
    void loop();
  }

  async function loop() {
    let errors = 0;
    while (!done && sid) {
      try {
        const r = await request(`poll?sid=${encodeURIComponent(sid)}&ack=${ack}`);
        if (r.status === 410) return finish(CLOSE_GONE);
        if (!r.ok) throw new Error(String(r.status));
        const body = (await r.json()) as { m: [number, ServerMessage][]; closed?: number };
        for (const [seq, msg] of body.m) {
          if (seq <= ack) continue; // повтор после потерянного ответа
          ack = seq;
          if (done) return;
          h.message(msg);
        }
        if (body.closed !== undefined) return finish(body.closed);
        errors = 0;
      } catch {
        if (done) return;
        if (++errors >= 4) return finish(1006);
        await sleep(500 * errors);
      }
    }
  }

  // команды уходят по одной очереди: порядок сохраняется
  async function flush() {
    if (sending || !sid || done) return;
    sending = true;
    try {
      while (outbox.length && !done) {
        const batch = outbox.slice(0, 20);
        let delivered = false;
        for (let attempt = 0; attempt < 3 && !delivered && !done; attempt++) {
          try {
            const r = await request(`send?sid=${encodeURIComponent(sid)}`, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ messages: batch }),
            });
            if (r.status === 410) return finish(CLOSE_GONE);
            delivered = r.ok;
          } catch {
            /* повтор: игровые действия идемпотентны по actionId */
          }
          if (!delivered) await sleep(400 * (attempt + 1));
        }
        if (!delivered) return finish(1006);
        outbox.splice(0, batch.length);
      }
    } finally {
      sending = false;
    }
  }

  void start();
  return {
    kind: 'poll',
    url: base,
    send: (m) => {
      if (done) return;
      outbox.push(m);
      void flush();
    },
    dispose: () => {
      if (done) return;
      const id = sid;
      done = true;
      ctrl.abort();
      if (id) navigator.sendBeacon?.(`${base}/close?sid=${encodeURIComponent(id)}`);
    },
  };
}
